import {
    DomainOverview,
    Language,
    LanguageOverview,
    Role,
    RoleOverview,
    Subject,
    SubjectOverview,
    Tool,
    ToolOverview,
} from 'types/wiki'
import Api from './api'
import {
    ApiResponse,
    /** Auth */
    AuthData,
    LoginProps,
    LoginResponse,
    VerifyEmailResponse,
    ExchangeGoogleTokenResponse,
    /** User */
    CreateUserProps,
    CreateUserResponse,
    EditUserDataProps,
    EditUserResponse,
    GetUserResponse,
    /** Billing */
    GetStripePricesResponse,
    CreateStripeSubscriptionProps,
    CreateStripeSubscriptionResponse,
    CreateStripeCheckoutSessionProps,
    CreateStripeCheckoutSessionResponse,
    VerifyCouponResponse,
    /** Path */
    GetPathResponse,
    GetPathsProgressResponse,
    GetPathsOverviewResponse,
    GetArticleResourceResponse,
    GetExternalResourceResponse,
    GetInformationalResourceResponse,
    GetCategoryResponse,
    GetCategoriesResponse,
    GetPathProgressResponse,
    GetQuizResourceResponse,
    GetQuizResultsResponse,
    GetResourceProps,
    GetResourcesOverviewResponse,
    GetLessonResourceResponse,
    GetProjectResourceResponse,
    GetVideoResourceResponse,
    MarkResourseAsDoneProps,
    GetPathNextResourceResponse,
    /** Tracks and Courses */
    GetCourseResponse,
    GetCourseProgressResponse,
    GetCoursesOverviewResponse,
    GetCoursesProgressResponse,
    SetResourceCompletionStatusResponse,
} from './types'
import { handleError } from './utils/devv-sdk-error'

interface DevvSdkProps {
    apiUrl: string
}

class DevvSdk {
    private static instance: DevvSdk
    private api: Api | undefined
    private userData?: AuthData | null
    private userId?: string | null

    constructor({ apiUrl }: DevvSdkProps) {
        if (!DevvSdk.instance) {
            DevvSdk.instance = this
        }

        DevvSdk.instance.api = new Api({ apiUrl })

        return DevvSdk.instance
    }

    public static getInstance(): DevvSdk {
        if (DevvSdk.instance === null || !(DevvSdk.instance instanceof DevvSdk)) {
            throw new Error('Devv SDK not initialized yet')
        }

        return DevvSdk.instance
    }

    public setAuthData(authData: { token: string; userId?: string }): void {
        DevvSdk.instance.api?.setAuthData(authData)
        if (authData.userId) {
            DevvSdk.instance.userId = authData.userId
        }
    }

    public deleteAuthData(): void {
        DevvSdk.instance.api?.deleteAuthData()
        DevvSdk.instance.userData = undefined
    }

    /** Auth */

    public async loginEmailPassword({ email, password }: LoginProps): Promise<LoginResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `auth/login`,
                method: 'POST',
                data: {
                    email,
                    password,
                },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_LOGIN', error)
        }
    }

    public async exchangeGoogleToken(value: string): Promise<ExchangeGoogleTokenResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `auth/google/token`,
                method: 'POST',
                data: {
                    id_token: value,
                },
            })
            return resp?.data
        } catch (error) {
            throw handleError('ERR_EXCHANGING_GOOGLE_TOKEN', error)
        }
    }

    /** User */

    public async createUser({ email, password, first_name, last_name }: CreateUserProps): Promise<CreateUserResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `users`,
                method: 'POST',
                data: {
                    email,
                    password,
                    first_name,
                    last_name,
                },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_CREATE_USER', error)
        }
    }

    public async getUser(): Promise<GetUserResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `users/${DevvSdk.instance.userId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GET_USER', error)
        }
    }

    public async getUserById(userId: string): Promise<GetUserResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `users/${userId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GET_USER_BY_ID', error)
        }
    }

    public async editUserData(props: EditUserDataProps): Promise<EditUserResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `users/${DevvSdk.instance.userId}`,
                method: 'PATCH',
                data: {
                    ...props,
                },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_EDIT_USER_DATA', error)
        }
    }

    public async startPasswordReset(value: string): Promise<void> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `users/password/reset`,
                method: 'PUT',
                data: {
                    email: value,
                },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_STARTING_PASSWORD_RESET', error)
        }
    }

    public async passwordReset(value: { token: string; password: string }): Promise<void> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `users/password`,
                method: 'PATCH',
                data: {
                    ...value,
                },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_RESETTING_PASSWORD', error)
        }
    }

    public async sendVerificationEmail(email: string): Promise<void> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `users/email/verify`,
                method: 'PUT',
                data: { email },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_SENDING_VERIFICATION_EMAIL', error)
        }
    }

    public async verifyEmail(token: string): Promise<VerifyEmailResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `users/email/verify/${token}`,
                method: 'PUT',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_VERIFYING_EMAIL', error)
        }
    }

    public async emailReset(value: string): Promise<void> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `users/${DevvSdk.instance.userId}/email/reset`,
                method: 'PUT',
                data: {
                    email: value,
                },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_RESET_MAIL', error)
        }
    }

    /** Sorting Quiz */

    public async getQuizResults(typformResponseId: string): Promise<GetQuizResultsResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `sorting-quiz/results/${typformResponseId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_QUIZ_RESULTS', error)
        }
    }

    /** Categories */

    public async getCategories(): Promise<GetCategoriesResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `categories`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_PATH_CATEGORIES', error)
        }
    }

    public async getCategoryBySlug(value: string): Promise<GetCategoryResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `categories/by-slug/${value}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_PATH_BY_SLUG', error)
        }
    }

    /** Paths */

    public async getPathsOverview(query?: {
        live?: boolean
        categories?: string[]
    }): Promise<GetPathsOverviewResponse> {
        try {
            const defaultParams = { published: true }

            const params = JSON.parse(
                JSON.stringify({
                    ...defaultParams,
                    categories: query?.categories ? JSON.stringify(query.categories) : undefined,
                    live: query?.live,
                }),
            )

            const resp = await DevvSdk.instance.api?.call({
                url: `paths/overview`,
                method: 'GET',
                params,
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_PATHS_OVERVIEW', error)
        }
    }

    public async getPathsProgress(): Promise<GetPathsProgressResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `paths/progress`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_PATHS_PROGRESS', error)
        }
    }

    /** Path */

    public async getPathById(pathId: string): Promise<GetPathResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `paths/${pathId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_PATH', error)
        }
    }

    public async getPathBySlug(slug: string): Promise<GetPathResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `paths/by-slug/${slug}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_PATH_BY_SLUG', error)
        }
    }

    public async getPathProgress(pathId: string): Promise<GetPathProgressResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `paths/${pathId}/progress`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_PATH_PROGRESS', error)
        }
    }

    public async enrollUserInPath(pathId: string, email?: string): Promise<void> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `paths/${pathId}/enroll`,
                method: 'PUT',
                data: { email },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_ENROLLING_USER_IN_PATH', error)
        }
    }

    public async preEnrollUserInPath(pathId: string, email?: string): Promise<void> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `paths/${pathId}/pre-enroll`,
                method: 'PUT',
                data: { email },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_PRE_ENROLLING_USER_IN_PATH', error)
        }
    }

    public async getPathNextResource(props: {
        pathId: string
        currentResourceId: string
    }): Promise<GetPathNextResourceResponse> {
        try {
            const { pathId, currentResourceId } = props

            const resp = await DevvSdk.instance.api?.call({
                url: `paths/${pathId}/resource-after/${currentResourceId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_NEXT_RESOURCE', error)
        }
    }

    /** Tracks And Courses */

    public async getCoursesOverview(query?: {
        live?: boolean
        categories?: string[]
    }): Promise<GetCoursesOverviewResponse> {
        try {
            const defaultParams = { course: true, published: true }

            const params = JSON.parse(
                JSON.stringify({
                    ...defaultParams,
                    categories: query?.categories ? JSON.stringify(query.categories) : undefined,
                    live: query?.live,
                }),
            )

            const resp = await DevvSdk.instance.api?.call({
                url: `tracks/overview`,
                method: 'GET',
                params,
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_TRACKS_OVERVIEW', error)
        }
    }

    public async getCoursesProgress(): Promise<GetCoursesProgressResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `tracks/progress`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_COURSES_PROGRESS', error)
        }
    }

    public async getCourseById(courseId: string): Promise<GetCourseResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `tracks/${courseId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_COURSE', error)
        }
    }

    public async getCourseBySlug(slug: string): Promise<GetCourseResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `tracks/by-slug/${slug}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_COURSE_BY_SLUG', error)
        }
    }

    public async getCourseProgress(courseId: string): Promise<GetCourseProgressResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `tracks/${courseId}/progress`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_COURSE_PROGRESS', error)
        }
    }

    public async enrollUserInCourse(courseId: string, email?: string): Promise<void> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `tracks/${courseId}/enroll`,
                method: 'PUT',
                data: { email },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_ENROLLING_USER_IN_COURSE', error)
        }
    }

    public async preEnrollUserInCourse(courseId: string, email?: string): Promise<void> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `tracks/${courseId}/pre-enroll`,
                method: 'PUT',
                data: { email },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_PRE_ENROLLING_USER_IN_COURSE', error)
        }
    }

    public async getCourseNextResource(props: {
        courseId: string
        currentResourceId: string
    }): Promise<GetPathNextResourceResponse> {
        try {
            const { courseId, currentResourceId } = props

            const resp = await DevvSdk.instance.api?.call({
                url: `tracks/${courseId}/resource-after/${currentResourceId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_NEXT_RESOURCE', error)
        }
    }

    /** Resources */

    public async getResourcesOverview(resources: string[]): Promise<GetResourcesOverviewResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `resources/overview`,
                method: 'GET',
                params: {
                    ids: JSON.stringify(resources),
                },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_RESOURCES_OVERVIEW', error)
        }
    }

    public async setResourceCompletionStatus(
        props: MarkResourseAsDoneProps,
    ): Promise<SetResourceCompletionStatusResponse> {
        try {
            const { resourceId, done } = props

            const resp = await DevvSdk.instance.api?.call({
                url: `resources/${resourceId}/${done ? 'done' : 'todo'}`,
                method: 'PUT',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_MARK_RESOURCE', error)
        }
    }

    public async getInformationalResourceDetails(props: GetResourceProps): Promise<GetInformationalResourceResponse> {
        const { resourceId } = props
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `resources/informationals/${resourceId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_INFORMATIONAL_RESOURCES', error)
        }
    }

    public async getArticleResourceDetails(props: GetResourceProps): Promise<GetArticleResourceResponse> {
        const { resourceId } = props
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `resources/articles/${resourceId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_ARTICLE_RESOURCES', error)
        }
    }

    public async getLessonResourceDetails(lessonId: string): Promise<GetLessonResourceResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `resources/lessons/${lessonId}`,
            })
            return resp?.data
        } catch (error) {
            throw handleError('ERR_GET_RESOURCE_LESSON', error)
        }
    }

    public async getVideoResourceDetails(props: GetResourceProps): Promise<GetVideoResourceResponse> {
        const { resourceId } = props
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `resources/videos/${resourceId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_VIDEO_RESOURCES', error)
        }
    }

    public async getExternalResourceDetails(props: GetResourceProps): Promise<GetExternalResourceResponse> {
        const { resourceId } = props
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `resources/external-resources/${resourceId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_EXTERNAL_RESOURCES', error)
        }
    }

    public async getQuizResourceDetails(props: GetResourceProps): Promise<GetQuizResourceResponse> {
        const { resourceId } = props
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `resources/quizzes/${resourceId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_QUIZ_RESOURCE', error)
        }
    }

    public async getProjectResourceDetails(props: GetResourceProps): Promise<GetProjectResourceResponse> {
        const { resourceId } = props
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `resources/projects/${resourceId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_PROJECT_RESOURCE', error)
        }
    }

    public async setExerciseCheckpointCompletionStatus(
        exerciseId: string,
        done: boolean,
        checkpointId?: string,
    ): Promise<SetResourceCompletionStatusResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `exercises/${exerciseId}/checkpoints/${done ? 'done' : 'todo'}`,
                method: 'PUT',
                data: {
                    checkpoint: checkpointId,
                },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_SET_EXERCISE_CHECKPOINT_STATUS', error)
        }
    }

    public async getExerciseProgress(
        exerciseId: string,
    ): Promise<ApiResponse<{ exercise: string; completed: boolean; checkpoints_completed: string[] }>> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `exercises/${exerciseId}/progress`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GET_EXERCISE_PROGRESS', error)
        }
    }

    public async getLessonProgress(lessonId: string): Promise<
        ApiResponse<{
            exercises: {
                _id: string
                completed: boolean
                checkpoints_completed: string[]
            }[]
            completed: boolean
            current_exercise: string | null
            current_exercise_number: number | null
        }>
    > {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `resources/lessons/${lessonId}/progress`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GET_LESSON_PROGRESS', error)
        }
    }

    public async getProjectProgress(
        projectId: string,
    ): Promise<ApiResponse<{ completed: boolean; tasks_completed: string[] }>> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `resources/${projectId}/progress`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GET_PROJECT_PROGRESS', error)
        }
    }

    public async setProjectTaskCompletionStatus(
        projectId: string,
        done: boolean,
        taskId: string,
    ): Promise<SetResourceCompletionStatusResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `resources/projects/${projectId}/tasks/${taskId}/${done ? 'done' : 'todo'}`,
                method: 'PUT',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_SET_PROJECT_TASK_STATUS', error)
        }
    }

    public async setResourceStarted(resourceId: string): Promise<void> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `resources/${resourceId}/start`,
                method: 'PUT',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_SETTING_RESOURCE_STARTED', error)
        }
    }

    public async canUserAccessResource(props: { resourceId: string }): Promise<ApiResponse<{ can_access: boolean }>> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `users/${DevvSdk.instance.userId}/can-access/${props.resourceId}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GET_CAN_ACCESS_RESOURCE', error)
        }
    }

    /** Billing */

    public async getStripePrices(): Promise<GetStripePricesResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `billing/prices`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_STRIPE_PRICES', error)
        }
    }

    public async verifyCoupon(coupon: string): Promise<VerifyCouponResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `billing/promo-codes/${coupon}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_VERIFY_COUPON', error)
        }
    }

    public async createStripeSubscription(
        props: CreateStripeSubscriptionProps,
    ): Promise<CreateStripeSubscriptionResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `billing/stripe/subscriptions`,
                method: 'POST',
                data: {
                    ...props,
                },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_CREATING_STRIPE_SUBSCRIPTION', error)
        }
    }

    public async createStripeCheckoutSession(
        props: CreateStripeCheckoutSessionProps,
    ): Promise<CreateStripeCheckoutSessionResponse> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `billing/stripe/checkout-sessions`,
                method: 'POST',
                data: {
                    ...props,
                },
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_CREATING_STRIPE_CHECKOUT_SESSION', error)
        }
    }

    /** Wiki */

    public async getDomainsOverview(query?: { limit?: number }): Promise<ApiResponse<DomainOverview[]>> {
        try {
            const defaultParams = {}

            const params = JSON.parse(
                JSON.stringify({
                    ...defaultParams,
                    ...query,
                }),
            )

            const resp = await DevvSdk.instance.api?.call({
                url: `wiki/domains/overview`,
                method: 'GET',
                params,
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_DOMAINS_OVERVIEW', error)
        }
    }

    public async getRolesOverview(query?: { limit?: number }): Promise<ApiResponse<RoleOverview[]>> {
        try {
            const defaultParams = {}

            const params = JSON.parse(
                JSON.stringify({
                    ...defaultParams,
                    ...query,
                }),
            )

            const resp = await DevvSdk.instance.api?.call({
                url: `wiki/roles/overview`,
                method: 'GET',
                params,
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_DOMAINS_OVERVIEW', error)
        }
    }

    public async getRoles(query?: { limit?: number }): Promise<ApiResponse<Role[]>> {
        try {
            const defaultParams = {}

            const params = JSON.parse(
                JSON.stringify({
                    ...defaultParams,
                    ...query,
                }),
            )

            const resp = await DevvSdk.instance.api?.call({
                url: `wiki/roles`,
                method: 'GET',
                params,
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_ROLES', error)
        }
    }

    public async getRoleBySlug(slug: string): Promise<ApiResponse<Role>> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `wiki/roles/by-slug/${slug}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_ROLE_BY_SLUG', error)
        }
    }

    public async getLanguagesOverview(query?: { limit?: number }): Promise<ApiResponse<LanguageOverview[]>> {
        try {
            const defaultParams = {}

            const params = JSON.parse(
                JSON.stringify({
                    ...defaultParams,
                    ...query,
                }),
            )

            const resp = await DevvSdk.instance.api?.call({
                url: `wiki/languages/overview`,
                method: 'GET',
                params,
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_LANGUAGES_OVERVIEW', error)
        }
    }

    public async getLanguageBySlug(slug: string): Promise<ApiResponse<Language>> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `wiki/languages/by-slug/${slug}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_LANGUAGE_BY_SLUG', error)
        }
    }

    public async getToolsOverview(query?: { limit?: number }): Promise<ApiResponse<ToolOverview[]>> {
        try {
            const defaultParams = {}

            const params = JSON.parse(
                JSON.stringify({
                    ...defaultParams,
                    ...query,
                }),
            )

            const resp = await DevvSdk.instance.api?.call({
                url: `wiki/tools/overview`,
                method: 'GET',
                params,
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_TOOLS_OVERVIEW', error)
        }
    }

    public async getToolBySlug(slug: string): Promise<ApiResponse<Tool>> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `wiki/tools/by-slug/${slug}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_TOOL_BY_SLUG', error)
        }
    }

    public async getSubjectsOverview(query?: { limit?: number }): Promise<ApiResponse<SubjectOverview[]>> {
        try {
            const defaultParams = {}

            const params = JSON.parse(
                JSON.stringify({
                    ...defaultParams,
                    ...query,
                }),
            )

            const resp = await DevvSdk.instance.api?.call({
                url: `wiki/subjects/overview`,
                method: 'GET',
                params,
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_SUBJECTS_OVERVIEW', error)
        }
    }

    public async getSubjectBySlug(slug: string): Promise<ApiResponse<Subject>> {
        try {
            const resp = await DevvSdk.instance.api?.call({
                url: `wiki/subjects/by-slug/${slug}`,
                method: 'GET',
            })

            return resp?.data
        } catch (error) {
            throw handleError('ERR_GETTING_SUBJECT_BY_SLUG', error)
        }
    }
}

const devvSdk = new DevvSdk({ apiUrl: process.env.NEXT_PUBLIC_DEVV_API_URL || '' })

export default devvSdk
